import { Attachment } from '@mui/icons-material';
import { Container, IconButton, styled, Tooltip, Typography } from '@mui/material';
import { GridFilterModel, GridPaginationModel, GridSortModel } from '@mui/x-data-grid';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import FileIndex from '../components/Files/FileIndex';
import FileTable from '../components/Files/FileTable';
import FileUploadDialog from '../components/Files/FileUploadDialog';
import ProjectGraphVisualization from '../components/GraphVisualization/ProjectGraphVisualization';
import LearnMore from '../components/LearnMore';
import LoadingMask from '../components/LoadingMask';
import { AnalyticsEvent, useAnalytics } from '../components/Providers/AnalyticsProvider';
import { useFetchData } from '../hooks/useFetchData';
import { getFolderFiles, getUserFiles } from '../redux/actions/fileActions';
import { getIndexedFiles } from '../redux/actions/indexActions';
import { myFolder } from '../redux/consts';
import { selectAppStatus } from '../redux/reducers/appStatusReducer';
import { setCurrentConversation } from '../redux/reducers/conversationReducer';
import { selectFiles } from '../redux/reducers/fileReducer';
import { selectFolders } from '../redux/reducers/folderReducer';
import { selectIndexes } from '../redux/reducers/indexReducer';
import { AppDispatch } from '../redux/store';
import { StyledTab, StyledTabs } from '../theme/CustomComponents';
import { AppEnum, FilesTabs, FileView, IndexType } from '../Types/enums';
import type { GraphData } from '../Types/graph';
import { graphRagVisualizationPathUrl } from '../util/routesUrls';

export type FilesProps = {
  selectedView?: FilesTabs;
};

const FilesDiv = styled('div')(() => {
  return {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    marginTop: '20px',
  };
});

const SectionCard = styled('div')(() => ({
  marginTop: '20px',
}));

const Files = ({ selectedView = FilesTabs.UPLOADED }: FilesProps): JSX.Element => {
  const dispatch = useDispatch<AppDispatch>();
  const analytics = useAnalytics();
  const { userFiles, totalUserFiles, folderFiles, totalFolderFiles } = useSelector(selectFiles);
  const { indexFiles } = useSelector(selectIndexes);
  const { currentFolder } = useSelector(selectFolders);
  const { selectedApp } = useSelector(selectAppStatus);
  const isIntellio = selectedApp === AppEnum.DEAL_GPT;
  const defaultSaveLocation = useRef<'user' | 'folder'>('user');
  const [isLoadingThis, setIsLoadingThis] = useState<boolean>(false);
  const [shouldRefetchUserFiles, setShouldRefetchUserFiles] = useState<boolean>(true);
  const [fileDialogOpen, setFileDialogOpen] = useState<boolean>(false);
  const [userPaginationModel, setUserPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: currentFolder && currentFolder.id !== myFolder.id ? 5 : 10,
  });
  const [userFilterModel, setUserFilterModel] = useState<GridFilterModel>({ items: [] });
  const [userSortModel, setUserSortModel] = useState<GridSortModel>([]);
  const [shouldRefetchFolderFiles, setShouldRefetchFolderFiles] = useState<boolean>(true);
  const [folderPaginationModel, setFolderPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 5,
  });
  const [folderFilterModel, setFolderFilterModel] = useState<GridFilterModel>({ items: [] });
  const [folderSortModel, setFolderSortModel] = useState<GridSortModel>([]);
  const projectId = currentFolder?.projectId ?? '';
  const [intellioView, setIntellioView] = useState<FilesTabs>(selectedView);
  const project = currentFolder?.project;
  const indexToUse = project?.indexToUse;
  const indexId = currentFolder?.indexIds?.[0];
  const [selectedGraphDocumentId, setSelectedGraphDocumentId] = useState<string | undefined>(
    undefined
  );
  const navigate = useNavigate();

  // Only sync tab with route
  useEffect(() => {
    setIntellioView(selectedView ?? FilesTabs.UPLOADED);
  }, [selectedView]);

  useEffect(() => {
    const shouldFetchIndexedFilesForGraph =
      isIntellio &&
      intellioView === FilesTabs.GRAPH_RAG_VISUALIZATION &&
      Boolean(indexId) &&
      indexToUse &&
      [IndexType.GRAPH_RAG, IndexType.OPENAI].includes(indexToUse) &&
      (!indexFiles || indexFiles.length === 0);

    if (!shouldFetchIndexedFilesForGraph) return;

    dispatch(
      getIndexedFiles({
        indexId: indexId as string,
        indexType: indexToUse,
        page: 0,
      })
    );
  }, [indexFiles, dispatch, indexId, indexToUse, intellioView, isIntellio]);

  const graphIndexId = currentFolder?.indexIds?.[0] || '';
  const {
    data: graphData,
    loading: isGraphLoading,
    error: graphError,
    refetch: refetchGraph,
  } = useFetchData<GraphData>({
    urlPath: graphRagVisualizationPathUrl(graphIndexId),
    skip: !currentFolder?.indexIds?.[0] || project?.indexToUse !== IndexType.GRAPH_RAG,
  });

  useEffect(() => {
    if (shouldRefetchUserFiles) {
      setIsLoadingThis(true);
      dispatch(
        getUserFiles({
          page: userPaginationModel.page,
          pageSize: userPaginationModel.pageSize,
          sortField: userSortModel[0]?.field,
          sortDirection: userSortModel[0]?.sort,
          filterModel: userFilterModel.items.length > 0 ? userFilterModel : undefined,
        })
      )
        .unwrap()
        .then(() => {
          setShouldRefetchUserFiles(false);
        })
        .finally(() => {
          setIsLoadingThis(false);
        });
    }
  }, [
    shouldRefetchUserFiles,
    dispatch,
    setShouldRefetchUserFiles,
    userPaginationModel,
    userSortModel,
    userFilterModel,
  ]);

  useEffect(() => {
    if (shouldRefetchFolderFiles && currentFolder && currentFolder.id !== myFolder.id) {
      setIsLoadingThis(true);
      dispatch(
        getFolderFiles({
          folderId: currentFolder?.id,
          page: folderPaginationModel.page,
          pageSize: folderPaginationModel.pageSize,
          sortField: folderSortModel[0]?.field,
          sortDirection: folderSortModel[0]?.sort,
          filterModel: folderFilterModel.items.length > 0 ? folderFilterModel : undefined,
        })
      )
        .unwrap()
        .then(() => {
          setShouldRefetchFolderFiles(false);
        })
        .finally(() => {
          setIsLoadingThis(false);
        });
    }
  }, [
    shouldRefetchFolderFiles,
    dispatch,
    setShouldRefetchFolderFiles,
    folderPaginationModel,
    folderSortModel,
    folderFilterModel,
    currentFolder,
  ]);

  const refetchFiles = useCallback(
    (shouldRefetch: boolean) => {
      setShouldRefetchUserFiles(shouldRefetch);
      setShouldRefetchFolderFiles(shouldRefetch);
    },
    [setShouldRefetchFolderFiles, setShouldRefetchUserFiles]
  );

  const handleGoToGraph = (graphDocId: string) => {
    setSelectedGraphDocumentId(graphDocId);
    setIntellioView(FilesTabs.GRAPH_RAG_VISUALIZATION);
    navigate(`/folder/${currentFolder?.id}/files/graph`);
  };

  const canShowProjectTabs =
    isIntellio &&
    Boolean(projectId) &&
    indexToUse &&
    [IndexType.GRAPH_RAG, IndexType.OPENAI].includes(indexToUse);

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        flexDirection: 'column',
        overflowY: 'auto',
        height: '100%',
      }}
    >
      <LoadingMask isLoading={isLoadingThis} />
      <FileUploadDialog
        userFiles={userFiles}
        folderFiles={folderFiles}
        setShouldRefetchFiles={refetchFiles}
        openDialog={fileDialogOpen}
        isLoading={isLoadingThis}
        setIsLoading={setIsLoadingThis}
        buttonConfirmText="Process Uploaded Documents"
        handleCloseDialog={() => {
          analytics.event(AnalyticsEvent.WM_NIGEL_MY_FILES_UPLOAD_DIALOG_CLOSE);
          setFileDialogOpen(false);
        }}
        defaultSaveLocationIsUser={defaultSaveLocation.current === 'user'}
      />
      <Container
        sx={{ bgcolor: 'white', height: 'fit-content', margin: '0px', marginBottom: '20px' }}
        style={{ padding: '0px' }}
      >
        {isIntellio && (
          <SectionCard style={{ marginTop: '0px' }}>
            <StyledTabs
              value={intellioView}
              onChange={(_, newView: FilesTabs) => {
                let viewUrl = '';

                if (newView === FilesTabs.INDEXED) {
                  viewUrl = '/index';
                } else if (newView === FilesTabs.GRAPH_RAG_VISUALIZATION) {
                  setSelectedGraphDocumentId(undefined);
                  viewUrl = '/graph';
                }
                setIntellioView(newView);
                navigate(`/folder/${currentFolder?.id}/files${viewUrl}`);
              }}
              sx={{ borderBottom: '1px solid #C4C4C4' }}
            >
              <StyledTab value={FilesTabs.UPLOADED} label="Uploaded Files" />
              <StyledTab
                value={FilesTabs.INDEXED}
                label="Indexed Files"
                disabled={!canShowProjectTabs}
              />
              {project?.indexToUse === IndexType.GRAPH_RAG && (
                <StyledTab
                  value={FilesTabs.GRAPH_RAG_VISUALIZATION}
                  label="Knowledge Graph Visualization"
                  disabled={!canShowProjectTabs || !currentFolder?.indexIds?.[0]}
                />
              )}
            </StyledTabs>

            {!projectId ? (
              <Typography variant="body2" sx={{ color: '#6B7280', marginTop: '12px' }}>
                Uploads are shown below in “Accessible Folder Files” and “My Files”.
              </Typography>
            ) : (
              <>
                {intellioView === FilesTabs.INDEXED && canShowProjectTabs && (
                  <div style={{ marginTop: '16px' }}>
                    <FileIndex
                      indexToUse={indexToUse}
                      indexId={indexId ?? ''}
                      onGoToGraph={handleGoToGraph}
                    />
                  </div>
                )}

                {intellioView === FilesTabs.GRAPH_RAG_VISUALIZATION && (
                  <div style={{ marginTop: '16px' }}>
                    <ProjectGraphVisualization
                      graphIndexId={currentFolder?.indexIds?.[0] || ''}
                      graphData={graphData}
                      isLoading={isGraphLoading}
                      error={graphError}
                      refetch={refetchGraph}
                      documentId={selectedGraphDocumentId}
                      onClearDocumentFilter={() => setSelectedGraphDocumentId(undefined)}
                      filesForDocumentFilter={indexFiles}
                      onDocumentFilterChange={(docId) => setSelectedGraphDocumentId(docId)}
                    />
                  </div>
                )}
              </>
            )}
          </SectionCard>
        )}

        {intellioView === FilesTabs.UPLOADED && (
          <>
            {currentFolder && currentFolder.id !== myFolder.id && (
              <FilesDiv>
                <div style={{ display: 'flex' }}>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    Accessible Folder Files
                  </Typography>
                  <LearnMore
                    link="https://howto.westmonroe.com/a/1988771-leveraging-documents-in-nigel-vs-intellio-advantage"
                    sx={{ ml: '10px', mt: '5px' }}
                  />
                  <Tooltip
                    PopperProps={{ placement: 'top' }}
                    id={'chat-upload-document-label'}
                    title={<Typography variant="caption">Add More Documents</Typography>}
                  >
                    <IconButton
                      component="label"
                      style={{ left: '8px' }}
                      aria-labelledby={'chat-upload-document-label'}
                      onClick={() => {
                        analytics.event(
                          AnalyticsEvent.WM_NIGEL_MY_FILES_ADD_MORE,
                          {},
                          { folderId: currentFolder.id }
                        );
                        defaultSaveLocation.current = 'folder';
                        setFileDialogOpen(true);
                      }}
                    >
                      <Attachment />
                    </IconButton>
                  </Tooltip>
                </div>
                <div>
                  <FileTable
                    files={folderFiles}
                    fileView={FileView.FOLDER}
                    setIsLoadingThis={setIsLoadingThis}
                    total={totalFolderFiles}
                    paginationModel={folderPaginationModel}
                    onPaginationModelChange={(newModel: GridPaginationModel) => {
                      setFolderPaginationModel(newModel);
                      setShouldRefetchFolderFiles(true);
                    }}
                    filterModel={folderFilterModel}
                    onFilterModelChange={(newModel: GridFilterModel) => {
                      setFolderFilterModel(newModel);
                      setFolderPaginationModel({ ...folderPaginationModel, page: 0 });
                      setShouldRefetchFolderFiles(true);
                    }}
                    sortModel={folderSortModel}
                    onSortModelChange={(newModel: GridSortModel) => {
                      setFolderSortModel(newModel);
                      setShouldRefetchFolderFiles(true);
                    }}
                    isLoading={isLoadingThis}
                    onFileChange={() => {
                      dispatch(setCurrentConversation(undefined));
                      refetchFiles(true);
                    }}
                  />
                </div>
              </FilesDiv>
            )}
            <FilesDiv>
              <div style={{ display: 'flex' }}>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  My Files
                </Typography>
                <Tooltip
                  PopperProps={{ placement: 'top' }}
                  id={'chat-upload-document-label'}
                  title={<Typography variant="caption">Add More Documents</Typography>}
                >
                  <IconButton
                    component="label"
                    style={{ left: '8px' }}
                    aria-labelledby={'chat-upload-document-label'}
                    onClick={() => {
                      analytics.event(AnalyticsEvent.WM_NIGEL_MY_FILES_ADD_MORE);
                      defaultSaveLocation.current = 'user';
                      setFileDialogOpen(true);
                    }}
                  >
                    <Attachment />
                  </IconButton>
                </Tooltip>
              </div>
              <FileTable
                files={userFiles}
                fileView={FileView.USER}
                setIsLoadingThis={setIsLoadingThis}
                total={totalUserFiles}
                paginationModel={userPaginationModel}
                onPaginationModelChange={(newModel: GridPaginationModel) => {
                  setUserPaginationModel(newModel);
                  setShouldRefetchUserFiles(true);
                }}
                filterModel={userFilterModel}
                onFilterModelChange={(newModel: GridFilterModel) => {
                  setUserFilterModel(newModel);
                  setUserPaginationModel({ ...userPaginationModel, page: 0 });
                  setShouldRefetchUserFiles(true);
                }}
                sortModel={userSortModel}
                onSortModelChange={(newModel: GridSortModel) => {
                  setUserSortModel(newModel);
                  setShouldRefetchUserFiles(true);
                }}
                isLoading={isLoadingThis}
                onFileChange={() => {
                  dispatch(setCurrentConversation(undefined));
                  refetchFiles(true);
                }}
              />
            </FilesDiv>
          </>
        )}
      </Container>
    </div>
  );
};

export default Files;
